/* Temporary menu latency diagnostics; never changes click dispatch or Meta. */
(() => {
  'use strict';
  const EXPIRES = Date.UTC(2026, 10, 3);
  const supported = window.PerformanceObserver?.supportedEntryTypes || [];
  if (window.dmMenuRumReady || Date.now() >= EXPIRES || !supported.includes('event') || Math.random() >= 0.25) return;
  window.dmMenuRumReady = true;
  const mobile = window.matchMedia('(max-width: 1024px)');
  const groups = new Map();
  const frames = [];
  let remaining = 10;
  let flushTimer;
  let eventObserver;
  let frameObserver;
  let active = false;
  const allowed = () => {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch (_) { return false; }
  };
  const bucket = (ms) => ms < 200 ? 'under200' : ms < 500 ? '200to499' : '500plus';
  const source = (raw) => {
    if (!raw) return 'unknown';
    try {
      const url = new URL(raw, location.origin);
      if (url.hostname === 'connect.facebook.net') return 'meta';
      if (url.hostname === 'scripts.clarity.ms' || url.hostname === 'www.clarity.ms') return 'clarity';
      if (url.origin === location.origin && /\/cdn\/shop\/t\/[^/]+\/assets\//.test(url.pathname)) return 'theme';
      if (url.origin === location.origin && url.pathname === location.pathname) return 'inline';
      return 'other';
    } catch (_) { return 'unknown'; }
  };
  // Capture only the fixed action and timestamp before navigation hides its view.
  // Never retain the DOM node, event, text or visitor data.
  const captureClick = (event) => {
    if (!active || !event.isTrusted || !mobile.matches || document.visibilityState !== 'visible') return;
    if (!allowed() || Date.now() >= EXPIRES || !remaining) { stop(); return; }
    const button = event.target instanceof Element ? event.target.closest('[data-dm-menu-metric]') : null;
    if (!button?.closest('.menu-links--mobile')) return;
    const action = button.dataset.dmMenuMetric;
    if (action !== 'products' && action !== 'back') return;
    groups.set(event.timeStamp, { action, entry: null });
    while (groups.size > 20) groups.delete(groups.keys().next().value);
    if (flushTimer === undefined) flushTimer = window.setTimeout(flush, 1000);
  };
  const stop = () => {
    active = false;
    window.removeEventListener('click', captureClick, true);
    eventObserver?.disconnect();
    frameObserver?.disconnect();
    clearTimeout(flushTimer);
    flushTimer = undefined;
    groups.clear();
    frames.length = 0;
  };
  const flush = () => {
    flushTimer = undefined;
    if (!allowed() || Date.now() >= EXPIRES || !remaining) { stop(); return; }
    if (!mobile.matches || document.visibilityState !== 'visible') { groups.clear(); return; }
    for (const [id, group] of groups) {
      groups.delete(id);
      const entry = group.entry;
      if (!entry) {
        // No Event Timing entry is not a duration: do not label it under200.
        if (typeof window.clarity === 'function') {
          try {
            window.clarity('event', ['dm_menu_v2', group.action, 'unmeasured'].join('_'));
            remaining -= 1;
          } catch (_) { /* Diagnostics must never interrupt the storefront. */ }
        }
        if (!remaining) { stop(); return; }
        continue;
      }
      const input = Math.max(0, entry.processingStart - entry.startTime);
      const processing = Math.max(0, entry.processingEnd - entry.processingStart);
      const presentation = Math.max(0, entry.startTime + entry.duration - entry.processingEnd);
      const phase = input >= processing && input >= presentation ? 'input' : processing >= presentation ? 'processing' : 'presentation';
      let script = frameObserver ? 'none' : 'unavailable';
      let largest = 0;
      for (const frame of frames) {
        if (frame.start >= entry.startTime + entry.duration || frame.end <= entry.startTime) continue;
        for (const item of frame.scripts) {
          if (item.ms > largest) { largest = item.ms; script = item.source; }
        }
      }
      // One bounded event keeps dimensions correlated; session tags would accumulate old values.
      if (typeof window.clarity === 'function') {
        try {
          window.clarity('event', ['dm_menu_v2', group.action, bucket(entry.duration), phase, script].join('_'));
          remaining -= 1;
        } catch (_) { /* Diagnostics must never interrupt the storefront. */ }
      }
      if (!remaining) { stop(); return; }
    }
  };
  const start = () => {
    if (!allowed() || Date.now() >= EXPIRES || !remaining) { stop(); return; }
    if (active || !mobile.matches) return;
    active = true;
    if (supported.includes('long-animation-frame')) {
      try {
        frameObserver = new PerformanceObserver((list) => {
          if (!allowed() || Date.now() >= EXPIRES) { stop(); return; }
          for (const frame of list.getEntries()) {
            frames.push({ start: frame.startTime, end: frame.startTime + frame.duration,
              scripts: (frame.scripts || []).map((item) => ({ ms: item.duration, source: source(item.sourceURL) })) });
          }
          frames.splice(0, Math.max(0, frames.length - 30));
        });
        frameObserver.observe({ type: 'long-animation-frame' });
      } catch (_) { frameObserver = undefined; }
    }
    eventObserver = new PerformanceObserver((list) => {
      if (!allowed() || Date.now() >= EXPIRES) { stop(); return; }
      if (!mobile.matches || document.visibilityState !== 'visible') return;
      for (const entry of list.getEntries()) {
        if (entry.name !== 'click' || !entry.interactionId) continue;
        // Event timestamps share the performance time origin. Small tolerance
        // accommodates clock precision; ambiguity is discarded, never guessed.
        const matches = [...groups].filter(([stamp]) => Math.abs(stamp - entry.startTime) <= 1);
        if (matches.length !== 1) continue;
        const group = matches[0][1];
        if (!group.entry || entry.duration > group.entry.duration) group.entry = entry;
      }
    });
    try { eventObserver.observe({ type: 'event', durationThreshold: 16 }); }
    catch (_) { stop(); return; }
    window.addEventListener('click', captureClick, { capture: true, passive: true });
  };
  document.addEventListener('visitorConsentCollected', start);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') {
      clearTimeout(flushTimer);
      flushTimer = undefined;
      groups.clear();
      frames.length = 0;
    }
  });
  mobile.addEventListener('change', () => { stop(); if (mobile.matches) start(); });
  if (window.Shopify?.customerPrivacy) start();
  else if (typeof window.Shopify?.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => { if (!error) start(); });
  }
})();
